<script setup>
// Topics → Map tab. A relationship graph of the taxonomy (Domain → Subject →
// Topic → deck) rendered with vis-network, which is heavy, so it's imported
// dynamically — this view is its own lazy chunk (like the Library items grid).
// Nodes/edges come from buildGraph (lib/categoryGraph.js, unit-tested); clicking
// a deck node launches the focused player.
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { buildGraph } from "../../lib/categoryGraph.js";

const router = useRouter();
const container = ref(null);
const loading = ref(true);
const error = ref("");
let network = null; // non-reactive — vis owns its own canvas state

const playHref = (slug) => `/topics/play/${encodeURIComponent(slug)}`;

function toVisNodes(nodes) {
  return nodes.map((n) =>
    n.kind === "deck"
      ? {
          id: n.id, label: n.label, shape: "box", margin: 8,
          color: { background: n.color, border: n.color, highlight: { background: n.color, border: "#1e293b" } },
          font: { color: "#ffffff" }
        }
      : {
          id: n.id,
          label: n.deckCount != null ? `${n.label}  (${n.deckCount})` : n.label,
          shape: "box", margin: 8, borderWidth: 2,
          shapeProperties: { borderRadius: 6 },
          color: { background: "#ffffff", border: n.color, highlight: { background: "#f8fafc", border: n.color } },
          font: { color: "#1e293b" }
        }
  );
}
function toVisEdges(edges) {
  return edges.map((e) => ({
    from: e.from, to: e.to, arrows: "to",
    color: { color: "#cbd5e1", highlight: "#94a3b8" },
    smooth: { type: "cubicBezier", forceDirection: "horizontal", roundness: 0.5 }
  }));
}

onMounted(async () => {
  try {
    const [cRes, dRes] = await Promise.all([
      fetch("/api/categories", { credentials: "include" }),
      fetch("/api/decks", { credentials: "include" })
    ]);
    if (!dRes.ok) throw new Error(`/api/decks responded ${dRes.status}`);
    const categories = cRes.ok ? (await cRes.json()).categories || [] : [];
    const decks = (await dRes.json()).decks || [];
    const graph = buildGraph(categories, decks);

    if (graph.nodes.length === 0) {
      error.value = "No topics to map yet.";
      return;
    }

    // Heavy dep — load only when this tab is opened.
    const vis = await import("vis-network/standalone");
    network = new vis.Network(
      container.value,
      { nodes: new vis.DataSet(toVisNodes(graph.nodes)), edges: new vis.DataSet(toVisEdges(graph.edges)) },
      {
        layout: { hierarchical: { enabled: true, direction: "LR", sortMethod: "directed", levelSeparation: 210, nodeSpacing: 80, treeSpacing: 130 } },
        physics: false,
        interaction: { hover: true, dragNodes: false, zoomView: true, dragView: true, tooltipDelay: 120 },
        nodes: { widthConstraint: { maximum: 190 } }
      }
    );

    const slugById = Object.fromEntries(graph.nodes.filter((n) => n.kind === "deck").map((n) => [n.id, n.slug]));
    network.on("click", (params) => {
      const id = params.nodes?.[0];
      if (id && slugById[id]) router.push(playHref(slugById[id]));
    });
  } catch (e) {
    console.error("[topics] map failed:", e);
    error.value = error.value || "We couldn't render the map just now.";
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  if (network) { network.destroy(); network = null; }
});
</script>

<template>
  <div class="card border-0 shadow-sm">
    <div class="card-body">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <p class="text-muted small mb-0">
          The topic map — Domain → Subject → Topic → deck. Click a deck to practise; scroll to zoom, drag to pan.
        </p>
      </div>

      <div v-if="loading" class="text-muted py-3">
        <span class="spinner-border spinner-border-sm me-2"></span>Building the map…
      </div>
      <div v-else-if="error" class="text-center text-muted py-4">
        <i class="bi bi-diagram-3 display-6 opacity-50 d-block mb-2"></i>
        <p class="mb-0">{{ error }}</p>
      </div>

      <!-- vis mounts here; kept in the DOM (v-show) so the ref exists during init -->
      <div v-show="!loading && !error" ref="container" class="rm-graph border rounded"></div>
    </div>
  </div>
</template>

<style scoped>
.rm-graph {
  height: calc(100vh - 260px);
  min-height: 420px;
  background: #fbfcfe;
}
</style>
